import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, ExternalLink, Handshake, Globe2, Ticket } from 'lucide-react';
import SEO from '../../components/SEO';
import StructuredData from '../../components/StructuredData';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import PressShareBar from '../../components/PressShareBar';

const SITE = 'https://staymii.com';
const PAGE = '/press/staymii-globaltix-mou-nice-2026';
const DATE_ISO = '2026-08-20';
const EVENT_ISO = '2026-08-27';
const OG_BANNER = SITE + '/og/staymii-globaltix-mou.jpg';
const HERO_IMAGE = '/og/staymii-globaltix-mou.jpg';
const GLOBALTIX_LOGO = '/ota-logos/globaltix.png';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'NewsArticle',
  headline: 'StayMii and GlobalTix to Sign Strategic MoU at NICE 2026, KLCC',
  description:
    'FOR IMMEDIATE RELEASE: StayMii today announces that it will sign a Memorandum of Understanding (MoU) with Singapore-based travel technology company GlobalTix on 27 August 2026 at the National Innovation and Commercialisation Expo (NICE 2026), Kuala Lumpur Convention Centre (KLCC).',
  datePublished: DATE_ISO,
  dateModified: DATE_ISO,
  mainEntityOfPage: SITE + PAGE,
  image: [OG_BANNER],
  author: { '@type': 'Organization', name: 'StayMii', url: SITE },
  publisher: {
    '@type': 'Organization',
    name: 'StayMii',
    url: SITE,
    logo: { '@type': 'ImageObject', url: SITE + '/og-image.jpg' },
  },
  keywords:
    'StayMii GlobalTix MoU, NICE 2026 KLCC, StayMii Global Market Connect, Malaysia tourism technology, attraction distribution, e-ticketing partnership, MOSTI MRANTI',
};

const eventSchema = {
  '@context': 'https://schema.org',
  '@type': 'Event',
  name: 'StayMii \u00d7 GlobalTix MoU Signing \u2014 NICE 2026',
  startDate: EVENT_ISO,
  eventStatus: 'https://schema.org/EventScheduled',
  eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
  location: {
    '@type': 'Place',
    name: 'Kuala Lumpur Convention Centre (KLCC)',
    address: { '@type': 'PostalAddress', addressLocality: 'Kuala Lumpur', addressCountry: 'MY' },
  },
  organizer: { '@type': 'Organization', name: 'StayMii', url: SITE },
  description:
    'Signing of a Memorandum of Understanding between StayMii and GlobalTix at the National Innovation and Commercialisation Expo (NICE 2026).',
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
    { '@type': 'ListItem', position: 2, name: 'Press', item: SITE + '/press' },
    { '@type': 'ListItem', position: 3, name: 'StayMii \u00d7 GlobalTix MoU at NICE 2026', item: SITE + PAGE },
  ],
};

export default function GlobalTixMouPressRelease() {
  return (
    <div className="min-h-screen bg-white" data-testid="globaltix-mou-press-release-page">
      <SEO
        title="StayMii and GlobalTix to Sign Strategic MoU at NICE 2026, KLCC | 27 August 2026"
        description="For immediate release: StayMii announces it will sign a Memorandum of Understanding with Singapore-based GlobalTix on 27 August 2026 at NICE 2026, KLCC, to expand global distribution for Malaysian attractions through the StayMii Global Market Connect (SGMC) Program."
        keywords="StayMii GlobalTix MoU, NICE 2026, KLCC, National Innovation and Commercialisation Expo, StayMii Global Market Connect, Malaysia attraction distribution, e-ticketing partnership"
        canonicalUrl={PAGE}
        ogType="article"
        noIndex={true}
        ogTitle="StayMii and GlobalTix Sign MOU at NICE 2026 to Strengthen Digital Ticketing & Tourism Distribution"
        ogDescription="StayMii and GlobalTix formalise a strategic collaboration at NICE 2026 to strengthen digital ticketing, attraction distribution and tourism technology connectivity."
        ogImage={OG_BANNER}
      />
      <StructuredData id="globaltix-mou-press" schema={[articleSchema, eventSchema, breadcrumbSchema]} />
      <LandingNavBar />

      <article className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <Link to="/press" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-6" data-testid="back-to-press-link">
          <ArrowLeft className="w-3.5 h-3.5" /> All press releases
        </Link>

        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 mb-4">
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> 20 August 2026</span>
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Kuala Lumpur, Malaysia</span>
          <span className="uppercase tracking-wider text-[10px] text-blue-700 font-semibold">For Immediate Release</span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 leading-tight">
          StayMii&trade; and GlobalTix to Sign Strategic Memorandum of Understanding at NICE 2026, KLCC, to Expand Global Distribution for Malaysian Attractions and Experiences
        </h1>

        <PressShareBar slug="staymii-globaltix-mou-nice-2026" title="StayMii and GlobalTix to Sign Strategic MoU at NICE 2026, KLCC" />

        <figure className="mb-8 -mx-6 sm:mx-0">
          <img
            src={HERO_IMAGE}
            alt="StayMii and GlobalTix strategic MoU signing at NICE 2026, Kuala Lumpur Convention Centre."
            className="w-full aspect-[16/10] object-cover sm:rounded-xl shadow-sm"
            loading="eager"
          />
          <figcaption className="text-xs text-slate-400 mt-2 px-6 sm:px-0">
            StayMii &times; GlobalTix &mdash; Memorandum of Understanding signing at NICE 2026, KLCC.
          </figcaption>
        </figure>

        <div className="prose prose-slate max-w-none">
          <p className="text-lg leading-relaxed"><strong>Kuala Lumpur, Malaysia &ndash; 20 August 2026</strong> &ndash; StayMii&trade; and GlobalTix, the Singapore-headquartered travel technology and distribution company, will formalise a strategic partnership through the signing of a Memorandum of Understanding (MoU) on <strong>27 August 2026 at the National Innovation and Commercialisation Expo (NICE 2026)</strong>, held at the Kuala Lumpur Convention Centre (KLCC).</p>

          <div className="not-prose my-8 rounded-xl border border-blue-200 bg-blue-50 p-5 flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="flex items-center gap-4 justify-center sm:justify-start">
              <span className="text-2xl font-extrabold text-[#0C2159]">StayMii</span>
              <Handshake className="w-6 h-6 text-blue-600" />
              <img src={GLOBALTIX_LOGO} alt="GlobalTix" className="h-9 w-auto object-contain" data-testid="globaltix-logo" />
            </div>
            <div className="flex-1 text-sm text-slate-600">
              <p><strong className="text-slate-900">Event:</strong> NICE 2026 &mdash; National Innovation and Commercialisation Expo (26&ndash;28 August 2026)</p>
              <p><strong className="text-slate-900">Signing date:</strong> 27 August 2026 &middot; <strong className="text-slate-900">Venue:</strong> KLCC, Kuala Lumpur</p>
            </div>
          </div>

          <p>The MoU marks a significant milestone in StayMii&rsquo;s mission to connect Malaysian attractions, museums, and experience operators to the global travel marketplace. By partnering with GlobalTix &mdash; whose distribution network reaches over 15,000 travel partners and powers more than 180,000 experiences worldwide &mdash; StayMii will extend the reach of local tourism products to international online travel agencies (OTAs), resellers, and destination management companies.</p>

          <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4">Signed at Malaysia&rsquo;s Premier Innovation Expo</h2>
          <p>NICE 2026, organised by the Ministry of Science, Technology and Innovation (MOSTI) and implemented by the Malaysian Research Accelerator for Technology and Innovation (MRANTI), runs under the theme <em>&ldquo;Mission to Market: Malaysia to the World.&rdquo;</em> The expo brings together innovators, industry leaders, investors, and policymakers to accelerate the commercialisation of Malaysian technology on the global stage &mdash; a fitting backdrop for a partnership designed to take Malaysian tourism products worldwide.</p>

          <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4">What the Partnership Enables</h2>
          <p>Under the intended collaboration, StayMii and GlobalTix will explore integration and joint distribution initiatives through the StayMii Global Market Connect (SGMC) Program, including:</p>
          <ul>
            <li>Global distribution of Malaysian attraction and experience inventory across GlobalTix&rsquo;s international reseller network</li>
            <li>Seamless connectivity between StayMii&rsquo;s e-ticketing and QR-Code visitor management and GlobalTix&rsquo;s channel-management platform</li>
            <li>Real-time inventory, availability, and pricing synchronisation across multiple sales channels</li>
            <li>Expanded market access and increased international visibility for Malaysian tourism operators</li>
          </ul>

          <div className="not-prose grid sm:grid-cols-2 gap-4 my-8">
            <div className="border border-slate-200 rounded-xl p-5 bg-slate-50 flex items-start gap-3">
              <Globe2 className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-slate-900 mb-1">Global reach</p>
                <p className="text-sm text-slate-600">Access to 15,000+ travel partners and 180,000+ experiences via GlobalTix&rsquo;s distribution network.</p>
              </div>
            </div>
            <div className="border border-slate-200 rounded-xl p-5 bg-slate-50 flex items-start gap-3">
              <Ticket className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-slate-900 mb-1">Connected ticketing</p>
                <p className="text-sm text-slate-600">StayMii e-ticketing and QR-Code admission, synced with global channels through SGMC.</p>
              </div>
            </div>
          </div>

          <blockquote className="border-l-4 border-blue-500 pl-4 italic text-slate-700 my-8">
            &ldquo;Signing this MoU with GlobalTix at NICE 2026 is a proud step forward in our mission to take Malaysian attractions and experiences to the world. Together, we can give local operators genuine global reach while keeping the visitor experience seamless from booking to admission.&rdquo;
            <footer className="not-italic text-sm text-slate-500 mt-2">&mdash; A representative of StayMii</footer>
          </blockquote>

          <p>The collaboration reflects a shared commitment between StayMii and GlobalTix to advance digital distribution, strengthen market connectivity, and support the growth and international competitiveness of Malaysia&rsquo;s tourism and experience economy.</p>

          <div className="grid sm:grid-cols-2 gap-6 mt-12 not-prose">
            <div className="border border-slate-200 rounded-xl p-5 bg-blue-50">
              <h3 className="text-sm font-bold text-slate-900 mb-2">About StayMii</h3>
              <p className="text-sm text-slate-600 leading-relaxed">StayMii is a Malaysian-developed, MySTI-certified tourism and hospitality technology platform providing integrated digital solutions for hotels, serviced apartments, museums, attractions, and tourism businesses &mdash; including LHDN-compliant e-invoicing, hospitality management, QR-Code e-ticketing, and digital market distribution through the StayMii Global Market Connect (SGMC) Program.</p>
              <Link to="/sgmc" className="inline-flex items-center gap-1 text-xs text-blue-700 font-semibold mt-3 hover:underline" data-testid="sgmc-cta-inline">
                Explore SGMC <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
            <div className="border border-slate-200 rounded-xl p-5 bg-slate-50">
              <h3 className="text-sm font-bold text-slate-900 mb-2">About GlobalTix</h3>
              <p className="text-sm text-slate-600 leading-relaxed">GlobalTix is a Singapore-headquartered travel technology company providing booking, distribution, and ticketing software for the tourism industry. Its cloud-based platform connects attractions, tours, and activity operators to a global network of travel partners, OTAs, and resellers, powering booking and distribution for experiences across the Asia-Pacific and beyond.</p>
              <a href="https://globaltix.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-700 font-semibold mt-3 hover:underline" data-testid="globaltix-website-link">
                globaltix.com <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          <div className="mt-12 border-t pt-6 text-sm text-slate-500">
            <p><strong className="text-slate-700">Media enquiries:</strong> <a href="mailto:media@staymii.com" className="text-blue-600 hover:underline">media@staymii.com</a></p>
            <p><strong className="text-slate-700">Partnerships / SGMC onboarding:</strong> <a href="mailto:partners@staymii.com" className="text-blue-600 hover:underline">partners@staymii.com</a></p>
            <p className="mt-4 text-xs text-slate-400 leading-relaxed" data-testid="press-trademark-notice">
              <strong>Trademark Notice:</strong> StayMii&trade; is a trademark pending registration with the Intellectual Property Corporation of Malaysia (MyIPO). GlobalTix and NICE are marks of their respective owners.
            </p>
          </div>
        </div>
      </article>

      <LandingFooter />
    </div>
  );
}
